
import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import ProductsHeader from "@/components/product/ProductsHeader";
import ProductList from "@/components/product/ProductList";
import EmptyProductState from "@/components/product/EmptyProductState";
import ProductsLoading from "@/components/product/ProductsLoading";
import ProductActions from "@/components/product/ProductActions";
import { useProducts } from "@/hooks/use-products";
import { useProductActions } from "@/hooks/use-product-actions";
import RetrospectiveUsageForm from "@/components/usage/RetrospectiveUsageForm";
import StockUpdateForm from "@/components/StockUpdateForm";
import UsageForm from "@/components/usage/UsageForm";
import OrderSuggestionsDialog from "@/components/ai/OrderSuggestionsDialog";
import { sortByUsageAndAlphabet, getProductUsageCount } from "@/lib/services/usageFrequencyService";
import { fetchGroups } from "@/lib/storage/group";
import { Group } from "@/lib/types";

type ProductCategory = 'all' | 'ostomy' | 'medicine' | 'other';

const Products = () => {
  const {
    products,
    isLoading,
    selectedProduct,
    setSelectedProduct,
    stockUpdateDialogOpen,
    setStockUpdateDialogOpen,
    usageDialogOpen,
    setUsageDialogOpen,
    refreshData
  } = useProducts();
  
  const [retrospectiveUsageDialogOpen, setRetrospectiveUsageDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<ProductCategory>('all');
  const [groups, setGroups] = useState<Group[]>([]);

  const {
    handleAddStock,
    handleRecordUsage,
    handleRecordPastUsage,
    handleQuickUsage,
    handleDeleteProduct
  } = useProductActions();

  // Fetch groups data
  useEffect(() => {
    const loadGroups = async () => {
      try {
        const groupsData = await fetchGroups();
        setGroups(groupsData);
      } catch (error) {
        console.error('Error loading groups:', error);
      }
    };
    
    loadGroups();
  }, []);

  // Filter products based on selected category
  const categoryFiltered = selectedCategory === 'all' 
    ? products 
    : products.filter(product => product.category === selectedCategory);

  // Sort filtered products by usage frequency, then alphabetically
  const filteredProducts = sortByUsageAndAlphabet(categoryFiltered, 'product', (product) => 
    getProductUsageCount(product.id)
  );

  if (isLoading) {
    return <ProductsLoading />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />

      <main className="page-container">
        <ProductsHeader 
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
        />
        
        <div className="flex justify-end mb-4">
          <OrderSuggestionsDialog groups={groups} products={products} />
        </div>

        {products.length === 0 ? (
          <EmptyProductState />
        ) : (
          <ProductList 
            products={filteredProducts}
            onUpdate={refreshData}
            onAddStock={(product) => handleAddStock(product, setSelectedProduct, setStockUpdateDialogOpen)}
            onRecordUsage={(product) => handleRecordUsage(product, setSelectedProduct, setUsageDialogOpen)}
            onRecordPastUsage={(product) => handleRecordPastUsage(product, setSelectedProduct, setRetrospectiveUsageDialogOpen)}
            onQuickUsage={(product) => handleQuickUsage(product, refreshData)}
            onDelete={(productId) => handleDeleteProduct(productId, products, refreshData)}
          />
        )}

        {/* Add the dialog components */}
        {selectedProduct && (
          <>
            <StockUpdateForm
              open={stockUpdateDialogOpen}
              onOpenChange={setStockUpdateDialogOpen}
              product={selectedProduct}
              onSuccess={refreshData}
            />
            
            <UsageForm
              open={usageDialogOpen}
              onOpenChange={setUsageDialogOpen}
              products={products}
              product={selectedProduct}
              onSuccess={refreshData}
            />
          </>
        )}
        
        <RetrospectiveUsageForm
          open={retrospectiveUsageDialogOpen}
          onOpenChange={setRetrospectiveUsageDialogOpen}
          products={products}
          onSuccess={refreshData}
        />
      </main>
    </div>
  );
};

export default Products;
